/**
 * Create RMA balance preview store.
 *
 * Fetches backend-calculated balance for a draft Create RMA request, used to block UI actions
 * when `blockCustomerInvoiceCreation` is enabled for a merchant.
 */

import { autorun, makeAutoObservable, runInAction } from "mobx";
import debounce from "lodash.debounce";
import CreateRmaService, { ICreateRmaBalancePreviewResultDto } from "../../../services/api/create-rma-service";
import { TYPE, rootContainer } from "../../../services/container/root";
import RmaRequestStore, { ICreateRmaDto } from "./rma-request-store";

export class CreateRmaBalancePreviewStore {
	private _callsCounter: number = 0;
	private _response?: ICreateRmaBalancePreviewResultDto;
	private _isInProgress: boolean = false;
	private _prevCompareString?: string;
	private _autorunDispose: () => void;
	private _debouncedFetchPreview: ReturnType<typeof debounce>;

	constructor(private _rmaRequestStore: RmaRequestStore) {
		this._debouncedFetchPreview = debounce(() => {
			void this._fetchPreview();
		}, 500);

		makeAutoObservable(this, { _debouncedFetchPreview: false } as any, { autoBind: true });

		this._prevCompareString = this._compareStringFromModel(this._rmaRequestStore.requestModel());
		this._autorunDispose = autorun(() => {
			if (this.shouldCheck()) {
				this.schedulePreview();
			}
		});
	}

	public __destroy(): void {
		this._autorunDispose();
		this._debouncedFetchPreview.cancel();
	}

	private get createRmaService(): CreateRmaService { return rootContainer.get<CreateRmaService>(TYPE.CreateRmaService); }

	public get isInProgress(): boolean { return this._isInProgress; }
	public get isCreateRmaBlocked(): boolean { return this._response?.is_create_rma_blocked ?? false; }

	/**
	 * There is nothing to preview without items, and the endpoint rejects an empty `items` list. The
	 * fee configuration is shared by every draft (FR7a), so a fee edit changes the compare string of
	 * the drafts the merchant selected nothing in too.
	 */
	private get _hasItems(): boolean { return this._rmaRequestStore.requestProducts.length > 0; }

	private shouldCheck(): boolean {
		const current = this._compareStringFromModel(this._rmaRequestStore.requestModel());
		const should = this._prevCompareString !== current;
		runInAction(() => {
			this._prevCompareString = current;
		});
		return should && this._hasItems;
	}

	/**
	 * Schedules a debounced preview request.
	 * Intended for continuous form edits; submit-time checks should use `checkPreview`.
	 */
	private schedulePreview(): void {
		runInAction(() => {
			this._isInProgress = true;
		});

		this._debouncedFetchPreview();
	}

	/**
	 * Forces an immediate preview request (cancels any scheduled debounce).
	 * Intended for submit-time blocking checks where we must await the latest result.
	 */
	public async checkPreview(): Promise<void> {
		this._debouncedFetchPreview.cancel();
		await this._fetchPreview();
	}

	private async _fetchPreview(): Promise<void> {
		const request = this._rmaRequestStore.requestModel();
		if (!request || !this._hasItems) {
			runInAction(() => {
				/** an emptied draft cannot block the submit on a balance it no longer has */
				this._response = undefined;
				this._isInProgress = false;
			});
			return;
		}

		this._callsCounter += 1;
		const currentCounter = this._callsCounter;
		let response: ICreateRmaBalancePreviewResultDto | undefined;

		try {
			runInAction(() => {
				this._isInProgress = true;
			});
			response = await this.createRmaService.previewBalance(request);
		} catch (e) {
			console.error(e);
		}

		if (currentCounter !== this._callsCounter) {
			return;
		}

		runInAction(() => {
			if (response) {
				this._response = response;
			}
			this._isInProgress = false;
		});
	}

	private _compareStringFromModel(dto: ICreateRmaDto): string | undefined {
		const { items, return_fee, label_fee, selected_dynamic_shipping_option_id } = dto as any;
		return JSON.stringify({ items, return_fee, label_fee, selected_dynamic_shipping_option_id });
	}
}
